import React from 'react'
import { State, createState, useState } from '@hookstate/core'
import { Validation as validation } from '@hookstate/validation'
import { Broadcasted as broadcasted } from '@hookstate/broadcasted'
import IDispatch from '../interfaces/IDispatch'
import IDispatchStatusActions from '../interfaces/IDispatchStatusActions'
import IDispatchState from '../interfaces/IDispatchState'
import useDispatchRelatedState from './DispatchRelatedState'
import { authFetch } from '~/services/fetch'
import { formatForSending, parseDataDispatch } from '../utils/parsers'
import { validPhone } from '~/utils/validators'
import { useLang } from '~/hooks/useLang'
import axios, { AxiosRequestConfig } from 'axios'
import notify from '~/utils/notify'
import IUnifyDispatch from '../interfaces/IUnifyDispatch'
import { getDispatchByIdService } from '~/features/Dispatch/services/index';
import { useMarkerState } from '~/features/Map/stores/MapState'
import { toast, type Id } from 'react-toastify'
import { useNotificationCenter } from 'react-toastify/addons/use-notification-center'
import useDispatchFormBuilderState from './useDispatchFormBuilderState'
import useDispatchVehicleState from './DispatchVehicleState'
import { LatLng } from 'leaflet'

type CopyDispatchProps = {
  readonly loading: State<boolean>
  readonly dispatchGroupsIds: readonly []
  readonly stateModal?: State<boolean>
}

export type CloseDispatchProps = {
  readonly closedStatusId: number | string
  readonly comment: string
  readonly operation: string | null
  readonly isConsummated: boolean | null
}

export const dispatchDefault: IDispatch = {
  id: null,
  code: null,
  inRouteAt: null,
  inPlaceAt: null,
  dispatchedAt: null,
  closedAt: null,
  hasNext: false,
  hasPrev: false,
  priority: 0,
  isConsummate: false,
  agency: {
    id: null,
    name: null,
    acronym: null,
  },
  closedStatus: {
    id: null,
    name: null,
    acronym: null,
  },
  dispatchGroup: {
    id: null,
    name: null,
    acronym: null,
  },
  type: {
    id: null,
    name: null,
    code: null,
    mustInformConsummate: false,
  },
  subtype: {
    id: null,
    name: null,
    code: null,
  },
  status: {
    id: null,
    name: null,
    acronym: null,
  },
  occurrence: {
    id: null,
    location: null,
    name: null,
    phone: null,
    address: null,
    latitude: null,
    longitude: null,
    entryAt: null,
    createdAt: null,
    calls: 1,
    toClerk: null,
    incidentId: null,
    createdUser: {
      id: null,
      representationName: null,
      registration: null,
    },
    operation: {
      id: null,
      name: null,
    },
    state: {
      id: null,
      name: null,
      uf: null,
    },
    city: {
      id: null,
      name: null,
    },
    district: {
      id: null,
      name: null,
    },
    entry_origin: {
      id: null,
      name: null,
    },
    entry_type: {
      id: null,
      name: null,
      code: null
    },
    entry_subtype: {
      id: null,
      name: null,
      code: null
    },
    incident: null,
    reference: null,
    featured: false,
  },
  hold: {
    id: null,
    occurrence_id: null,
    dispatch_id: null,
    was_communicated: null,
    holding_until_at: null,
    returned_at: null,
    created_user_id: null,
    returned_user_id: null,
    occurrence_comment_id: null,
    notes: null,
    device_id: null,
    created_station_id: null,
    returned_station_id: null,
    created_at: null,
    updated_at: null,
    deleted_at: null,
  },
  dispatchRetainHours: 0,
  dispatchRetainMinutes: 0,
  hasUnified: true,
  totalSubtypes: 0,
  totalDistricts: 0
}

const dispatchStatusActionsDefault: IDispatchStatusActions = {
  canCopy: true,
  canPrint: true,
  canRegistration: true,
  canReport: true,
  canClose: true,
  canCreate: true,
  canSave: false,
  canLocation: false,
  canMedia: false,
  canDispatchName: false,
  canAddVehicles: true,
  hasPrev: false,
  hasNext: false,
  canChronology: true,
  canHighlight: true,
  canReopen: true,
  canHold: true,
  canUnify: true,
  canDisunify: true,
  editingDispatch: false,
  editingDispatchRequester: false,
  isOpenUnification: false,
  isOpenRegistration: false,
  isOpenReport: false,
  isOpenDisunification: false,
  canClosePopup: false,
}

type SyncDispatchFromPayloadParams = {
  readonly dispatchParams: {
    readonly dispatch: IDispatch
    readonly hasNext?: boolean
    readonly hasPrev?: boolean
    readonly hasUnified?: boolean
  }
  readonly canSaveDispatch: boolean
  readonly canCreateDispatch: boolean
}

const dispatchStore = Object.create(dispatchDefault);

const store = createState<IDispatchState>({
  dispatch: dispatchStore,
  dispatcheHistoric: dispatchStore,
  dispatchStatusActions: dispatchStatusActionsDefault,
  openModalResultMultipleCloseDispatches: false,
  loadingDispatchForm: true,
})

store.attach(broadcasted('dispatch-events'))

// eslint-disable-next-line max-lines-per-function
export default function useDispatchState() {
  const dispatch = useState(store.dispatch);
  const dispatchHistoric = useState(store.dispatcheHistoric);
  const {
    id,
    type,
    subtype,
    agency,
    occurrence: {
      name,
      phone,
      id: occurrenceId,
      reference,
      location,
      city,
      district,
      latitude,
      longitude,
      calls,
    },
    totalSubtypes,
    totalDistricts
  } = dispatch;
  const { translate } = useLang();
  const openModalResultMultipleCloseDispatches = useState(store.openModalResultMultipleCloseDispatches)
  const loadingDispatchForm = useState(store.loadingDispatchForm)
  const dispatchStatusActions = useState(store.dispatchStatusActions);
  const { canCreate, canSave, hasPrev, hasNext, editingDispatch, editingDispatchRequester } = dispatchStatusActions;
  const { syncVehicleListInDispatch, setDefaultvehicleListInDispatch } = useDispatchVehicleState();
  const { dispatchRelated } = useDispatchRelatedState();
  const { deleteMarker, updateMarker } = useMarkerState();
  const { remove } = useNotificationCenter();


  id.attach(validation)
  validation(id).validate(
    () => Boolean(id.get() != null),
    'Se o ID está valido'
  )

  if (phone) {
    phone.attach(validation)
    validation(phone).validate(
      () => validPhone(phone.get()),
      'Telefone Completo'
    )
  }

  if (type.id) {
    type.id.attach(validation);
    validation(type.id).validate(() => Boolean(type.id.get() != null), 'Se o Tipo está valido');
  }

  if (subtype.id) {
    subtype.id.attach(validation);
    validation(subtype.id).validate(() => (totalSubtypes.get() > 0 ? totalSubtypes.get() > 0 && subtype.id.get() != null : true), 'Se precisa informar um subtipo');
  }

  if (district?.id) {
    district.id.attach(validation);
    validation(district.id).validate(() => (totalDistricts.get() > 0 ? totalDistricts.get() > 0 && district.id.get() != null : true), 'Se precisa informar um subtipo');
  }

  const syncVehicleDispatchState = async (id: number) => {
    await syncVehicleListInDispatch(id)
  }

  const setDefaultDispatch = () => {
    dispatch.merge({
      id: null,
      code: null,
      inRouteAt: null,
      inPlaceAt: null,
      dispatchedAt: null,
      closedAt: null,
      priority: 0,
      agency: {
        id: null,
        name: null,
        acronym: null,
      },
      closedStatus: {
        id: null,
        name: null,
        acronym: null,
      },
      dispatchGroup: {
        id: null,
        name: null,
        acronym: null,
      },
      type: {
        id: null,
        name: null,
        code: null,
        mustInformConsummate: false,
      },
      subtype: {
        id: null,
        name: null,
        code: null,
      },
      status: {
        id: null,
        name: null,
        acronym: null,
      },
      occurrence: {
        id: null,
        location: null,
        name: null,
        phone: null,
        address: null,
        latitude: null,
        longitude: null,
        entryAt: null,
        createdAt: null,
        calls: 1,
        toClerk: null,
        incidentId: null,
        createdUser: {
          id: null,
          representationName: null,
          registration: null,
        },
        operation: {
          id: null,
          name: null,
        },
        state: {
          id: null,
          name: null,
          uf: null,
        },
        city: {
          id: null,
          name: null,
        },
        district: {
          id: null,
          name: null,
        },
        entry_origin: {
          id: null,
          name: null,
        },
        entry_type: {
          id: null,
          name: null,
          code: null
        },
        entry_subtype: {
          id: null,
          name: null,
          code: null
        },
        incident: null,
        reference: null,
        featured: false,
      },
      hold: {
        id: null,
        occurrence_id: null,
        dispatch_id: null,
        was_communicated: null,
        holding_until_at: null,
        returned_at: null,
        created_user_id: null,
        returned_user_id: null,
        occurrence_comment_id: null,
        notes: null,
        device_id: null,
        created_station_id: null,
        returned_station_id: null,
        created_at: null,
        updated_at: null,
        deleted_at: null,
      },
      dispatchRetainHours: 0,
      dispatchRetainMinutes: 0,
      hasUnified: true,
      totalSubtypes: 0
    })
  }


  async function syncDispatchFromId(dispatchId: number | null | undefined) {
    setDefaultDispatch()
    const dispatchData: IDispatch | null = parseDataDispatch(await getDispatchByIdService(dispatchId))
    return syncDispatchFromPayload({
      dispatchParams: {
        dispatch: dispatchData,
        hasNext: dispatchData.hasNext,
        hasPrev: dispatchData.hasPrev
      },
      canCreateDispatch: true,
      canSaveDispatch: true,
    })

  }

  function syncDispatchFromPayload({
    dispatchParams: {
      dispatch: dispatchObj,
      hasPrev: hasPrevDispatch = true,
      hasNext: hasNextDispatch = false,
      hasUnified: hasUnifiedDispatch = false
    },
    canCreateDispatch = false,
    canSaveDispatch = true,
  }: SyncDispatchFromPayloadParams) {
    dispatch.set(dispatchObj)
    canCreate.set(canCreateDispatch)
    canSave.set(canSaveDispatch)
    hasPrev.set(hasPrevDispatch)
    hasNext.set(hasNextDispatch)
    editingDispatch.set(false);
    editingDispatchRequester.set(false);
    return true
  }

  function getRequestUrlForDispatchPosition(
    type: 'previous' | 'next' | 'last'
  ) {
    const suburl =
      type == 'previous'
        ? 'get-my-previous'
        : type == 'next'
          ? 'get-my-next'
          : 'get-my-last'
    return `/dispatch/${suburl}`
  }

  async function getMyDispatch(
    type: 'previous' | 'next' | 'last',
    currentDispatch: string | number | null = null
  ) {
    try {
      if (type !== 'last' && !currentDispatch) {
        throw new Error('Previous and next must specify an id on which it can based on')
      }

      // loadingDispatchForm.set(true)
      const requestUrl = getRequestUrlForDispatchPosition(type)
      const axiosRequest: AxiosRequestConfig<any> = {
        url: requestUrl,
        method: 'POST',
      }
      if (currentDispatch) axiosRequest['data'] = { idRef: currentDispatch }

      const response = await authFetch(axiosRequest)

      return syncDispatchFromPayload({
        dispatchParams: {
          dispatch: parseDataDispatch(response.data.dispatch),
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
          hasUnified: response.data.hasUnified,
        },
        canCreateDispatch: true,
        canSaveDispatch: true,
      })
    } catch (error) {
      console.error('Erro ao buscar despacho:', error)
      throw error
    } finally {
      loadingDispatchForm.set(false)
    }
  }

  return {
    dispatch: () => dispatch,
    dispatchHistoric: () => dispatchHistoric,
    dispatchStatusActions: () => dispatchStatusActions,
    openModalResultMultipleCloseDispatches: () => openModalResultMultipleCloseDispatches,
    loadingDispatchForm: loadingDispatchForm,
    syncDispatchFromId: syncDispatchFromId,
    setDefaultDispatch: setDefaultDispatch,
    syncDispatchFromPayload: syncDispatchFromPayload,
    getMyLastDispatch: () => getMyDispatch('last'),
    previousDispatch: () => getMyDispatch('previous', id.get()),
    nextDispatch: () => getMyDispatch('next', id.get()),
    saveDispatch: async () => {
      authFetch({
        url: `/dispatch/${id.get()}`,
        method: 'PUT',
        data: formatForSending(dispatch.get()),
      })
    },
    copyDispatch: async ({ dispatchGroupsIds, loading, stateModal }: CopyDispatchProps) => {
      loading.set(true)
      try {
        const response = await authFetch({
          url: '/dispatch/copy',
          method: 'POST',
          data: {
            dispatch_id: id.get(),
            dispatch_group_ids: dispatchGroupsIds,
          },
        })

        const { data } = response
        if (data) {
          data.map((dispatch) => {
            dispatchRelated().merge({
              [dispatch.id]: { id: dispatch.id, code: dispatch.code, status: dispatch.status },
            })
          })
          notify({
            message: `${translate('Dispatch copied successfully!', {
              code: data.code,
            })}`,
            type: 'success',
          })
        }
      } catch (error) {
        console.error('Erro ao copiar despacho:', error)
        throw error
      } finally {
        loading.set(false)
        stateModal?.set(false)
      }
    },
    closeDispatch: async (params: CloseDispatchProps) => {
      try {
        const response = await authFetch({
          url: '/dispatch/close',
          method: 'POST',
          data: {
            dispatch_id: id.get(),
            closed_status_id: params.closedStatusId,
            operation_id: params.operation,
            comment: params.comment,
            is_consummate: params.isConsummated
          },
        })

        if (response.status === 200) {
          toast.dismiss(id.get() as Id)
          remove(id.get() as Id)
        }

        // await getMyDispatch('last')

        notify({
          message: `${translate('Finished successfully!')}`,
          type: 'success',
        })

        return true

      } catch (error) {
        console.error('Erro ao fechar despacho:', error)
        throw error
      }
    },
    associateVehicleDispatch: async (deviceIds: readonly number[], loading?: State<boolean>) => {
      const dispatchId = id.get();
      loading?.set(true)
      if (deviceIds.length === 0) {
        loading?.set(false)
        console.error('DeviceId(s) Nulo');
        return false;
      }
      if (!dispatchId) {
        loading?.set(false)
        console.error('Dispatch Nulo');
        return false;
      }

      const data = { dispatch_id: dispatchId, device_ids: deviceIds }

      try {
        await authFetch({
          url: '/dispatch/associate',
          method: 'POST',
          data,
        });

        await syncVehicleDispatchState(dispatchId);

        notify({
          message: translate('Device associated to dispatch successfully'),
          type: 'success',
        });

        return true;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        loading?.set(false);
      }
    },
    reopenDispatch: async (loading?: State<boolean>) => {
      loading?.set(true)
      try {
        await authFetch({
          url: '/dispatch/reopen',
          method: 'POST',
          data: {
            dispatch_id: id.get(),
          },
        })

        await syncDispatchFromId(id.get())

        notify({
          message: `${translate('Reopened successfully!')}`,
          type: 'success',
        })
      } catch (error) {
        console.error('Erro ao reabrir despacho:', error)
        throw error
      } finally {
        loading?.set(false)
      }
    },
    holdDispatch: async (minutes, comment, notifyDispatchHolded, loading?: State<boolean>) => {
      loading?.set(true)
      try {
        await authFetch({
          url: '/dispatch/hold-dispatch',
          method: 'POST',
          data: {
            dispatch_id: id.get(),
            holding_until: minutes,
            notes: comment,
            notify: notifyDispatchHolded
          },
        })

        await syncDispatchFromId(id.get())

        notify({
          message: `${translate('Hold successfully!')}`,
          type: 'success',
        })
      } catch (error) {
        console.error('Erro ao segurar despacho:', error)
        throw error
      } finally {
        loading?.set(false)
      }
    },
    freeDispatch: async () => {
      try {
        await authFetch({
          url: '/dispatch/free-dispatch',
          method: 'POST',
          data: {
            dispatch_id: id.get(),
          },
        })

        await syncDispatchFromId(id.get())

        notify({
          message: `${translate('Free successfully!')}`,
          type: 'success',
        })
      } catch (error) {
        console.error('Erro ao liberar despacho:', error)
        throw error
      }
    },
    getDispatchByCode: (code: string, notOnlyVisibles = false) => {
      const cancelToken = axios.CancelToken.source()

      // change any-type just testing !
      const dispatchByCodePromise = new Promise<readonly IDispatch[]>(
        (resolve, reject) => {
          authFetch({
            url: '/dispatch/find',
            data: {
              code,
              not_only_visibles: notOnlyVisibles
            },
            method: 'POST',
            cancelToken: cancelToken.token,
          })
            .then(({ data, status }) => {
              if (!data.length)
                reject(
                  new Error(translate('Not Found Dispatch with this code'))
                )
              resolve(data)
            })
            .catch((err) => reject(err))
        }
      )

      return { promise: dispatchByCodePromise, cancelToken }
    },
    getDispatchById: getDispatchByIdService,
    updateDispatch: async () => {
      try {
        if (validation(type.id).valid() && validation(subtype.id).valid() && validation(district.id).valid()) {
          await authFetch({
            url: '/dispatch/address/change',
            method: 'POST',
            data: {
              dispatch_id: id.get(),
              address_reference: reference.get(),
              location: location.get(),
              city_id: city.id.get(),
              district_id: district.id.get(),
              latitude: latitude.get(),
              longitude: longitude.get(),
            },
          })

          const responseType = await authFetch({
            url: '/dispatch/' + id.get(),
            method: 'PUT',
            data: {
              type_id: type.id.get(),
              subtype_id: subtype.id.get(),
            },
          })

          updateMarker({
            markerType: 'dispatch',
            markerIdentifier: dispatch.id.get(),
            attribute: 'location',
            value: dispatch.occurrence.location.get()
          })

          notify({
            message: `${translate('Saved successfully!')}`,
            type: 'success',
          })

          editingDispatch.set(false)

          await syncDispatchFromId(id.get())

          if (responseType.data?.newDispatches?.length > 0) {
            notify({
              message: `Novos grupos adicionados ${responseType.data.newDispatches.map(element => element.code)}`,
              type: 'success',
            })
          }
        } else {
          notify({
            message: `${translate('Please check the form information and try again!')}`,
            type: 'error'
          })
        }
      } catch (error) {
        console.error('Erro ao atualizar despacho:', error)
        editingDispatch.set(false)
        throw error
      }
    },
    updateDispatchRequester: async () => {
      await authFetch({
        url: '/dispatch/change-requester',
        method: 'POST',
        data: {
          dispatch_id: dispatch.id.get(),
          name: name.get(),
          phone: phone.get()
        }
      })

      notify({
        message: `${translate('Saved successfully!')}`,
        type: 'success',
      })
      editingDispatchRequester.set(false)
    },
    unificationDispatch: async (param: IUnifyDispatch) => {
      try {
        const response = await authFetch({
          url: '/dispatch/unify',
          method: 'POST',
          data: {
            dispatch_origin_id: id.get(),
            dispatch_destination_id: param.dispatchId,
          },
        })

        await syncDispatchFromId(response.data.id)

        notify({
          message: `${translate('Dispatch unified successfully!')}`,
          type: 'success',
        })
      } catch (error) {
        console.error('Erro ao unificar despacho:', error)
        throw error
      }
    },
    disunificationDispatch: async ({ unificationId, dispatchUnifiedId }) => {
      try {
        await authFetch({
          url: '/dispatch/disunify',
          method: 'POST',
          data: {
            dispatch_unified_id: unificationId,
          },
        })

        await syncDispatchFromId(dispatchUnifiedId)

        notify({
          message: `${translate('Dispatch disunified successfully!')}`,
          type: 'success',
        })
      } catch (error) {
        console.error('Erro ao desunificar despacho:', error)
        throw error
      }
    },
    getTimelineItems: async () => {
      try {
        if (occurrenceId.get() != null) {
          const response = await authFetch({
            url: `/occurrence/${occurrenceId.get()}/timeline`,
            method: 'POST',
          })
          return response.data
        } else {
          return []
        }
      } catch (error) {
        console.error('Erro ao buscar timeline:', error)
        return []
      }
    },
    socketUpdateLocation: ({ dispatchId, geolocation }) => {
      if (dispatchId == id.get()) {
        location.set(geolocation.location);
        latitude.set(geolocation.latitude);
        longitude.set(geolocation.longitude);

        const newPosition = new LatLng(Number(latitude.get()), Number(longitude.get()));
        
        updateMarker({
          markerType: 'dispatch',
          markerIdentifier: dispatchId,
          attribute: 'position',
          value: newPosition,
        })

        updateMarker({
          markerType: 'dispatch',
          markerIdentifier: dispatchId,
          attribute: 'location',
          value: location.get(),
        })
      }
    },
    socketUpdateCallsDispatch: ({ callsNumber, dispatchId }) => {
      if (dispatchId == id.get()) {
        calls.set(callsNumber)
      }
    },
    printDispatch: (dispatchId: string | number | null) => {
      if (!dispatchId) return null;
      window.open(`/dispatch/print/${dispatchId}`)
    }
  }
}
